// Where a catalogue version's files come from. The edge's manifest may name `cdn_base`, the bucket's
// own custom domain (docs/design/catalogue-cdn.md): versioned files are fetched there first, off the
// Worker, and from the edge's `/v1/catalogue/<version>/<file>` when that fails. Only versioned keys go
// to the CDN; the manifest itself always comes from the edge, so a new version is noticed at once.
import { getEdgeBase } from "../backends";

const STORAGE_KEY = "proef.catalogue.cdnBase";

/** The base when it is a plain https URL, with no trailing slash; anything else is no CDN at all. */
function normalise(base: string | undefined): string | undefined {
  if (base === undefined) return undefined;
  try {
    const url = new URL(base);
    if (url.protocol !== "https:" || url.search !== "" || url.hash !== "") return undefined;
    return url.href.replace(/\/+$/, "");
  } catch {
    return undefined;
  }
}

/** The page's storage; none in the catalogue worker, which is handed its URLs in each plan. */
function storage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}

function readStored(): string | undefined {
  try {
    return normalise(storage()?.getItem(STORAGE_KEY) ?? undefined);
  } catch {
    // Blocked storage: no CDN until the next manifest names one.
    return undefined;
  }
}

/** Kept across reloads so a map drawn before the first manifest check also skips the Worker. */
let cdnBase: string | undefined = readStored();
/** Set once the CDN could not be reached at all this session, so later fetches go to the edge directly. */
let cdnUnreachable = false;

/** Records the manifest's `cdn_base` (or its absence, which turns the CDN off again). */
export function rememberCdnBase(base: string | undefined): void {
  cdnBase = normalise(base);
  try {
    if (cdnBase === undefined) storage()?.removeItem(STORAGE_KEY);
    else storage()?.setItem(STORAGE_KEY, cdnBase);
  } catch {
    // Not persisting only means the edge serves the files until the next manifest.
  }
}

/** The edge's URL of a version's file, resolved against the page; undefined where there is no page. */
export function edgeFileUrl(version: string, name: string): string | undefined {
  const page = typeof globalThis.location === "undefined" ? undefined : globalThis.location.href;
  try {
    return new URL(`${getEdgeBase()}/v1/catalogue/${version}/${name}`, page).href;
  } catch {
    return undefined;
  }
}

/** The CDN's URL of a version's file under `base`, the manifest's `cdn_base`. */
export function cdnFileUrl(
  base: string | undefined,
  version: string,
  name: string,
): string | undefined {
  const root = normalise(base);
  return root === undefined ? undefined : `${root}/${version}/${name}`;
}

/** Where to try a version's file, in order: the CDN when one is known and reachable, then the edge. */
export function catalogueFileUrls(version: string, name: string): string[] {
  const cdn = cdnUnreachable ? undefined : cdnFileUrl(cdnBase, version, name);
  const edge = edgeFileUrl(version, name);
  return [cdn, edge].filter((url): url is string => url !== undefined);
}

/**
 * The first answer that is ok, trying `urls` in order. A failure of any but the last (unreachable,
 * CORS, a 404 or 5xx) moves on to the next; the last one's answer, or its error, is the caller's.
 */
export async function fetchFirst(urls: readonly string[], init?: RequestInit): Promise<Response> {
  const last = urls.length - 1;
  if (last < 0) throw new TypeError("no URL to fetch the catalogue file from");
  for (const [index, url] of urls.entries()) {
    try {
      const response = await fetch(url, init);
      if (response.ok || index === last) return response;
      await response.body?.cancel();
    } catch (error) {
      if (index === last || init?.signal?.aborted === true) throw error;
      if (cdnBase !== undefined && url.startsWith(`${cdnBase}/`)) cdnUnreachable = true;
    }
  }
  throw new TypeError("unreachable");
}

/**
 * For an `<img>` whose CDN source failed to load: the edge's URL of the same file, or undefined when
 * `src` is not a CDN URL (so the image is not retried in a loop).
 */
export function edgeFallbackFor(src: string): string | undefined {
  if (cdnBase === undefined || !src.startsWith(`${cdnBase}/`)) return undefined;
  const [version, ...rest] = src.slice(cdnBase.length + 1).split("/");
  if (version === undefined || rest.length === 0) return undefined;
  return edgeFileUrl(version, rest.join("/"));
}

/** Test seam: start over with `base` as the known CDN (none by default), reachable. */
export function resetCdnForTests(base?: string): void {
  rememberCdnBase(base);
  cdnUnreachable = false;
}
